import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Table from "@/components/Base/Table";
import Button from "@/components/Base/Button";
import Pagination from "@/components/Base/Pagination";
import { FormSelect } from "@/components/Base/Form";
import Lucide from "@/components/Base/Lucide";
import Litepicker from "@/components/Base/Litepicker";
import { Dialog, Popover } from "@/components/Base/Headless";
import PageLoadingOverlay from "@/components/PageLoadingOverlay";
import { getToken } from "@/utils/auth";

type Team = { id: string; name: string; status?: boolean };
type BankInSlipRow = {
  teamSalesId: string;
  teamId: string;
  teamName: string | null;
  salesDate: string;
  bankedAt: string | null;
  bankInAmount: number | null;
  bankInDifference: number;
  remainingAmount: number;
  paidAmount: number;
  expectedTotal: number;
  bankInVerified: boolean;
  bankInVerifiedAt: string | null;
  receiptCount: number;
  addedByAdmin?: boolean;
};
type BankSlip = {
  id: string;
  imageUrl: string;
  s3Key: string;
  createdAt: string;
};
type BankPayment = {
  id: string;
  amount: number;
  remark: string | null;
  createdAt: string;
  supervisorName: string | null;
  bankSlips: BankSlip[];
};
type BankReceiptReason = {
  reason: string;
  createdAt: string;
  supervisorName: string | null;
};
type BankReceiptDetails = {
  teamSalesId: string;
  teamId: string;
  teamName: string | null;
  salesDate: string;
  bankedAt: string;
  bankInAmount: number | null;
  bankInDifference: number;
  remainingAmount?: number;
  paidAmount?: number;
  bankInVerified?: boolean;
  bankInVerifiedAt?: string | null;
  bankInVerifiedByName?: string | null;
  remark?: string | null;
  reasons?: BankReceiptReason[];
  receiptCount: number;
  payments: BankPayment[];
};

const SETTLED_REMAINING_EPS = 0.004;
const FILTERS_STORAGE_KEY = "bankInSlips.filters";
const PAGE_SIZES = [50, 250, 500, 1000] as const;

type SortKey = "team" | "salesDate" | "bankedAt" | "status" | "verified";
type SortDir = "asc" | "desc";
const SORT_KEYS: SortKey[] = ["team", "salesDate", "bankedAt", "status", "verified"];

type StoredFilters = {
  dateRange?: string;
  teamIds?: string[];
  sortKey?: SortKey;
  sortDir?: SortDir;
};

function isSortKey(value: unknown): value is SortKey {
  return typeof value === "string" && (SORT_KEYS as string[]).includes(value);
}

function isSortDir(value: unknown): value is SortDir {
  return value === "asc" || value === "desc";
}

function loadStoredFilters(): StoredFilters | null {
  try {
    const raw = localStorage.getItem(FILTERS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    const stored = parsed as StoredFilters;
    return {
      ...stored,
      sortKey: isSortKey(stored.sortKey) ? stored.sortKey : undefined,
      sortDir: isSortDir(stored.sortDir) ? stored.sortDir : undefined,
    };
  } catch {
    return null;
  }
}

function saveStoredFilters(filters: StoredFilters) {
  try {
    localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  } catch {
    // ignore storage errors
  }
}

function dateToYMD(v: Date) {
  return v.toISOString().slice(0, 10);
}

function parseDateRangeInput(value: string): { startDate: string; endDate: string } | null {
  const m = value.trim().match(/(\d{4}-\d{2}-\d{2})\s*-\s*(\d{4}-\d{2}-\d{2})/);
  if (!m) return null;
  return { startDate: m[1], endDate: m[2] };
}

function formatDateHeading(ymd: string) {
  const d = new Date(`${ymd}T00:00:00.000Z`);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(d);
}

function formatDateTime(v: string | null | undefined) {
  if (!v) return "-";
  const d = new Date(v);
  if (!Number.isFinite(d.getTime())) return "-";
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

function moneyWithThousands(v: number) {
  const n = Number(v || 0);
  if (!Number.isFinite(n)) return "0.00";
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function isSettled(row: BankInSlipRow) {
  return Number(row.remainingAmount || 0) <= SETTLED_REMAINING_EPS;
}

function isVerified(row: BankInSlipRow) {
  return isSettled(row) && Boolean(row.bankInVerified);
}

function SortableTh({
  label,
  column,
  sortKey,
  sortDir,
  onSort,
  className = "",
}: {
  label: string;
  column: SortKey;
  sortKey: SortKey;
  sortDir: SortDir;
  onSort: (column: SortKey) => void;
  className?: string;
}) {
  const active = sortKey === column;
  return (
    <Table.Th className={`border-b-0 whitespace-nowrap ${className}`}>
      <button
        type="button"
        onClick={() => onSort(column)}
        className="inline-flex items-center gap-1 font-medium hover:text-primary"
      >
        {label}
        {active ? (
          <Lucide icon={sortDir === "asc" ? "ChevronUp" : "ChevronDown"} className="w-3.5 h-3.5" />
        ) : (
          <Lucide icon="ChevronsUpDown" className="w-3.5 h-3.5 text-slate-400" />
        )}
      </button>
    </Table.Th>
  );
}

export default function BankInSlipsPage() {
  const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
  const token = getToken();

  const today = new Date();
  const monthStart = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
  const defaultDateRange = `${dateToYMD(monthStart)} - ${dateToYMD(today)}`;

  const [dateRange, setDateRange] = useState(() => {
    const stored = loadStoredFilters()?.dateRange?.trim();
    return stored && parseDateRangeInput(stored) ? stored : defaultDateRange;
  });
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(() => loadStoredFilters()?.teamIds ?? []);
  const [teamsReady, setTeamsReady] = useState(false);

  const [rows, setRows] = useState<BankInSlipRow[]>([]);
  const [sortKey, setSortKey] = useState<SortKey>(() => loadStoredFilters()?.sortKey ?? "salesDate");
  const [sortDir, setSortDir] = useState<SortDir>(() => loadStoredFilters()?.sortDir ?? "desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [appliedFilters, setAppliedFilters] = useState<{
    startDate: string;
    endDate: string;
    teamIds: string[];
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptLoading, setReceiptLoading] = useState(false);
  const [receiptError, setReceiptError] = useState("");
  const [receiptDetails, setReceiptDetails] = useState<BankReceiptDetails | null>(null);
  const [receiptVerifying, setReceiptVerifying] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${apiUrl}/teams?pageSize=500`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list: Team[] = Array.isArray(res.data?.data) ? res.data.data : [];
        const active = list.filter((t) => t.status !== false).sort((a, b) => a.name.localeCompare(b.name));
        setTeams(active);
        const storedIds = loadStoredFilters()?.teamIds;
        const validStored = Array.isArray(storedIds) ? storedIds.filter((id) => active.some((t) => t.id === id)) : [];
        setSelectedTeamIds(validStored.length ? validStored : active.map((t) => t.id));
      } catch {
        setTeams([]);
      } finally {
        setTeamsReady(true);
      }
    })();
  }, [apiUrl, token]);

  useEffect(() => {
    saveStoredFilters({ dateRange, teamIds: selectedTeamIds, sortKey, sortDir });
  }, [dateRange, selectedTeamIds, sortKey, sortDir]);

  const allTeamsSelected = teams.length > 0 && selectedTeamIds.length === teams.length;
  const teamFilterLabel = useMemo(() => {
    if (!teams.length || allTeamsSelected || selectedTeamIds.length === 0) return "All Teams";
    if (selectedTeamIds.length === 1) {
      return teams.find((t) => t.id === selectedTeamIds[0])?.name || "1 team";
    }
    return `${selectedTeamIds.length} teams`;
  }, [teams, selectedTeamIds, allTeamsSelected]);

  const onSort = (column: SortKey) => {
    if (sortKey === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(column);
      setSortDir(column === "team" ? "asc" : "desc");
    }
    setCurrentPage(1);
  };

  const loadData = useCallback(async () => {
    if (!appliedFilters) return;
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams();
      params.set("startDate", appliedFilters.startDate);
      params.set("endDate", appliedFilters.endDate);
      if (appliedFilters.teamIds.length) params.set("teamIds", appliedFilters.teamIds.join(","));
      params.set("page", String(currentPage));
      params.set("pageSize", String(pageSize));
      params.set("sortKey", sortKey);
      params.set("sortDir", sortDir);
      const res = await axios.get(`${apiUrl}/team-sales-data/bank-in-slips?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(Array.isArray(res.data?.rows) ? res.data.rows : []);
      const nextTotal = Number(res.data?.total ?? 0);
      const nextPageSize = Number(res.data?.pageSize ?? pageSize) || pageSize;
      const nextTotalPages = Math.max(
        1,
        Number(res.data?.totalPages ?? (Math.ceil(nextTotal / nextPageSize) || 1)),
      );
      setTotal(nextTotal);
      setTotalPages(nextTotalPages);
      const serverPage = Number(res.data?.page ?? currentPage);
      if (Number.isFinite(serverPage) && serverPage !== currentPage) setCurrentPage(serverPage);
    } catch (e: any) {
      setError(e?.response?.data?.message || "Failed to load bank-in slips.");
      setRows([]);
      setTotal(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token, appliedFilters, currentPage, pageSize, sortKey, sortDir]);

  const applyFilters = () => {
    const parsedRange = parseDateRangeInput(dateRange);
    if (!parsedRange) {
      setError("Please select a valid date range.");
      return;
    }
    if (selectedTeamIds.length === 0) {
      setError("Select at least one team, then apply filter.");
      return;
    }
    setError("");
    setSuccess("");
    setCurrentPage(1);
    setAppliedFilters({
      startDate: parsedRange.startDate,
      endDate: parsedRange.endDate,
      teamIds: allTeamsSelected ? [] : selectedTeamIds,
    });
  };

  const exportImages = async () => {
    if (!appliedFilters) return;
    setExporting(true);
    setError("");
    setSuccess("");
    try {
      const params = new URLSearchParams();
      params.set("startDate", appliedFilters.startDate);
      params.set("endDate", appliedFilters.endDate);
      if (appliedFilters.teamIds.length) params.set("teamIds", appliedFilters.teamIds.join(","));
      const res = await axios.get(`${apiUrl}/team-sales-data/bank-in-slips/export-images?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
        timeout: 300000,
      });
      const blob = new Blob([res.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `bank-in-slips-${appliedFilters.startDate}-to-${appliedFilters.endDate}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setSuccess("Bank-in slip images exported.");
    } catch (e: any) {
      let message = "Failed to export bank-in slip images.";
      const data = e?.response?.data;
      if (data instanceof Blob) {
        try {
          const parsed = JSON.parse(await data.text());
          if (parsed?.message) message = parsed.message;
        } catch {
          // keep default
        }
      } else if (e?.response?.data?.message) {
        message = e.response.data.message;
      }
      setError(message);
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    if (!teamsReady) return;
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamsReady]);

  useEffect(() => {
    if (!appliedFilters) return;
    void loadData();
  }, [loadData, appliedFilters]);

  const openReceiptDetails = async (row: BankInSlipRow) => {
    setReceiptModalOpen(true);
    setReceiptLoading(true);
    setReceiptError("");
    setReceiptDetails(null);
    setReceiptVerifying(false);
    try {
      const params = new URLSearchParams();
      params.set("teamId", row.teamId);
      params.set("salesDate", row.salesDate);
      const res = await axios.get(`${apiUrl}/team-sales-data/bank-receipts?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReceiptDetails((res.data?.data || null) as BankReceiptDetails | null);
    } catch (e: any) {
      setReceiptError(e?.response?.data?.message || "Failed to load bank receipt details.");
    } finally {
      setReceiptLoading(false);
    }
  };

  const verifyReceiptBankIn = async () => {
    if (!receiptDetails || receiptDetails.bankInVerified) return;
    const remaining = Number(receiptDetails.remainingAmount ?? 0);
    if (remaining > SETTLED_REMAINING_EPS) return;

    setReceiptVerifying(true);
    setReceiptError("");
    try {
      const res = await axios.post(
        `${apiUrl}/team-sales-data/verify-bank-in`,
        { teamId: receiptDetails.teamId, salesDate: receiptDetails.salesDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const verifiedAt = (res.data?.data?.bankInVerifiedAt as string | undefined) || new Date().toISOString();
      const verifiedByName = (res.data?.data?.bankInVerifiedByName as string | null | undefined) ?? null;
      setReceiptDetails((prev) =>
        prev
          ? { ...prev, bankInVerified: true, bankInVerifiedAt: verifiedAt, bankInVerifiedByName: verifiedByName }
          : prev
      );
      setRows((prev) =>
        prev.map((r) =>
          r.teamId === receiptDetails.teamId && r.salesDate === receiptDetails.salesDate
            ? { ...r, bankInVerified: true, bankInVerifiedAt: verifiedAt }
            : r
        )
      );
      setSuccess("Bank in slip verified.");
    } catch (e: any) {
      setReceiptError(e?.response?.data?.message || "Failed to verify bank in slip.");
    } finally {
      setReceiptVerifying(false);
    }
  };

  return (
    <ProtectedRoute>
      <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
        <div className="grid grid-cols-12 gap-y-10 gap-x-6 w-full">
          <PageLoadingOverlay show={loading} label="Loading bank-in slips…" />
          <div className="col-span-12">
            <div className="flex flex-col md:h-10 gap-y-3 md:items-center md:flex-row">
              <div className="text-base font-medium group-[.mode--light]:text-white">Bank-In Slips</div>
            </div>

            <div className="flex flex-col gap-8 mt-3.5">
              <div className="flex flex-col box box--stacked">
                <div className="flex flex-col p-5 sm:flex-row sm:items-end gap-3">
                  <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)_auto_auto] gap-3 w-full sm:w-auto">
                    <div className="sm:w-[420px]">
                      <div className="text-xs text-slate-500 mb-1">Date Range</div>
                      <div className="relative inline-flex w-full">
                        <Lucide
                          icon="Calendar"
                          className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                        />
                        <Litepicker
                          value={dateRange}
                          onChange={(e) => setDateRange((e.target?.value || "").trim())}
                          options={{
                            autoApply: true,
                            singleMode: false,
                            numberOfColumns: 2,
                            numberOfMonths: 2,
                            showWeekNumbers: true,
                            dropdowns: {
                              minYear: 2020,
                              maxYear: null,
                              months: true,
                              years: true,
                            },
                            format: "YYYY-MM-DD",
                          }}
                          className="w-full min-w-[220px] pl-9 pr-3 py-2 rounded-[0.5rem] text-sm"
                        />
                      </div>
                    </div>
                    <div className="sm:w-[240px]">
                      <div className="text-xs text-slate-500 mb-1">Team</div>
                      <Popover className="w-full">
                        <Popover.Button
                          as="button"
                          type="button"
                          className="w-full flex items-center justify-between gap-2 rounded-[0.5rem] border border-slate-300/80 dark:border-darkmode-400 bg-white dark:bg-darkmode-800 px-3 h-[38px] text-sm text-left"
                        >
                          <span className="truncate">{teamFilterLabel}</span>
                          <Lucide icon="ChevronDown" className="w-4 h-4 shrink-0 text-slate-500" />
                        </Popover.Button>
                        <Popover.Panel placement="bottom-start" className="w-72 p-0 overflow-hidden">
                          {({ close }: { close: () => void }) => (
                            <>
                              <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-200/70 dark:border-darkmode-400">
                                <button
                                  type="button"
                                  className="text-xs font-medium text-primary hover:underline"
                                  onClick={() => setSelectedTeamIds(teams.map((t) => t.id))}
                                >
                                  Select All
                                </button>
                                <span className="text-slate-300">|</span>
                                <button
                                  type="button"
                                  className="text-xs font-medium text-primary hover:underline"
                                  onClick={() => setSelectedTeamIds([])}
                                >
                                  Deselect All
                                </button>
                                <button
                                  type="button"
                                  className="ml-auto text-xs text-slate-500 hover:text-slate-700"
                                  onClick={() => close()}
                                >
                                  Done
                                </button>
                              </div>
                              <div className="max-h-64 overflow-y-auto py-1">
                                {teams.length === 0 ? (
                                  <div className="px-3 py-4 text-sm text-slate-500">No teams found.</div>
                                ) : (
                                  teams.map((t) => {
                                    const checked = selectedTeamIds.includes(t.id);
                                    return (
                                      <label
                                        key={t.id}
                                        className="flex items-center gap-2.5 px-3 py-1.5 text-sm cursor-pointer hover:bg-slate-50 dark:hover:bg-darkmode-500"
                                      >
                                        <input
                                          type="checkbox"
                                          className="rounded border-slate-300"
                                          checked={checked}
                                          onChange={() => {
                                            setSelectedTeamIds((prev) =>
                                              prev.includes(t.id) ? prev.filter((id) => id !== t.id) : [...prev, t.id]
                                            );
                                          }}
                                        />
                                        <span className="truncate flex-1">{t.name}</span>
                                      </label>
                                    );
                                  })
                                )}
                              </div>
                            </>
                          )}
                        </Popover.Panel>
                      </Popover>
                    </div>
                    <div className="sm:self-end">
                      <Button
                        variant="primary"
                        onClick={applyFilters}
                        disabled={loading || exporting || selectedTeamIds.length === 0}
                        className="whitespace-nowrap px-4"
                      >
                        Apply Filter
                      </Button>
                    </div>
                    <div className="sm:self-end">
                      <Button
                        variant="outline-secondary"
                        onClick={() => void exportImages()}
                        disabled={loading || exporting || total === 0 || !appliedFilters}
                        className="whitespace-nowrap px-4"
                      >
                        {exporting ? (
                          <>
                            <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                            Exporting...
                          </>
                        ) : (
                          <>
                            <Lucide icon="Download" className="w-4 h-4 mr-2" />
                            Export Bank In Slip Image
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
                {error ? <div className="px-5 pb-3 text-danger text-sm">{error}</div> : null}
                {success ? <div className="px-5 pb-3 text-success text-sm">{success}</div> : null}

                <div className="overflow-x-auto">
                  <Table className="border-spacing-y-[10px] border-separate -mt-2">
                    <Table.Thead>
                      <Table.Tr>
                        <SortableTh label="Team" column="team" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                        <SortableTh label="Sales Date" column="salesDate" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                        <SortableTh label="Submitted At" column="bankedAt" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                        <Table.Th className="border-b-0 whitespace-nowrap text-right">Paid (RM)</Table.Th>
                        <Table.Th className="border-b-0 whitespace-nowrap text-right">Expected (RM)</Table.Th>
                        <SortableTh label="Status" column="status" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                        <Table.Th className="border-b-0 whitespace-nowrap text-center">Slips</Table.Th>
                        <SortableTh label="Verified" column="verified" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
                        <Table.Th className="border-b-0 whitespace-nowrap text-right">Action</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {loading ? (
                        <Table.Tr>
                          <Table.Td colSpan={9} className="py-6 text-center text-slate-500">
                            Loading bank-in slips...
                          </Table.Td>
                        </Table.Tr>
                      ) : selectedTeamIds.length === 0 ? (
                        <Table.Tr>
                          <Table.Td colSpan={9} className="py-6 text-center text-slate-500">
                            Select at least one team, then apply filter.
                          </Table.Td>
                        </Table.Tr>
                      ) : rows.length === 0 ? (
                        <Table.Tr>
                          <Table.Td colSpan={9} className="py-6 text-center text-slate-500">
                            No bank-in slip records for this filter.
                          </Table.Td>
                        </Table.Tr>
                      ) : (
                        rows.map((row) => {
                          const settled = isSettled(row);
                          return (
                            <Table.Tr key={row.teamSalesId} className="[&_td]:last:border-b-0">
                              <Table.Td className="py-2 border-dashed dark:bg-darkmode-600 text-sm font-medium">
                                {row.teamName || "-"}
                              </Table.Td>
                              <Table.Td className="py-2 border-dashed dark:bg-darkmode-600 text-sm whitespace-nowrap">
                                {formatDateHeading(row.salesDate)}
                              </Table.Td>
                              <Table.Td className="py-2 border-dashed dark:bg-darkmode-600 text-sm whitespace-nowrap">
                                {formatDateTime(row.bankedAt)}
                              </Table.Td>
                              <Table.Td className="py-2 border-dashed dark:bg-darkmode-600 text-sm text-right tabular-nums">
                                {moneyWithThousands(row.paidAmount)}
                              </Table.Td>
                              <Table.Td className="py-2 border-dashed dark:bg-darkmode-600 text-sm text-right tabular-nums">
                                {moneyWithThousands(row.expectedTotal)}
                              </Table.Td>
                              <Table.Td className="py-2 border-dashed dark:bg-darkmode-600 text-sm">
                                <div className="flex flex-col items-start gap-1">
                                  {row.addedByAdmin ? (
                                    <span className="inline-flex rounded border border-primary/30 bg-primary/5 px-1.5 py-0.5 text-[11px] font-semibold text-primary">
                                      Added by CD admin
                                    </span>
                                  ) : settled ? (
                                    <span className="inline-flex rounded border border-green-200 bg-green-50 px-1.5 py-0.5 text-[11px] font-semibold text-green-800">
                                      Fully settled
                                    </span>
                                  ) : (
                                    <span className="inline-flex rounded border border-red-200 bg-red-50 px-1.5 py-0.5 text-[11px] font-semibold text-red-800">
                                      Amount owed: RM {moneyWithThousands(row.remainingAmount)}
                                    </span>
                                  )}
                                </div>
                              </Table.Td>
                              <Table.Td className="py-2 border-dashed dark:bg-darkmode-600 text-sm text-center tabular-nums">
                                {row.receiptCount}
                              </Table.Td>
                              <Table.Td className="py-2 border-dashed dark:bg-darkmode-600 text-sm">
                                {isVerified(row) ? (
                                  <span className="inline-flex items-center gap-1 text-green-600 font-medium">
                                    <Lucide icon="BadgeCheck" className="w-4 h-4 stroke-[2.25]" />
                                    Verified
                                  </span>
                                ) : (
                                  <span className="text-slate-400">Not verified</span>
                                )}
                              </Table.Td>
                              <Table.Td className="py-2 border-dashed dark:bg-darkmode-600 text-sm text-right">
                                <Button
                                  variant="outline-secondary"
                                  size="sm"
                                  className="px-2 py-1"
                                  onClick={() => void openReceiptDetails(row)}
                                >
                                  <Lucide icon="Eye" className="w-3.5 h-3.5 mr-1" />
                                  View
                                </Button>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })
                      )}
                    </Table.Tbody>
                  </Table>
                </div>
                <div className="flex flex-col-reverse flex-wrap items-center p-5 gap-y-2 sm:flex-row border-t border-slate-200/60">
                  <div className="text-sm text-slate-500 mr-4">
                    {total > 0 ? (
                      <>
                        Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, total)} of {total}{" "}
                        records
                      </>
                    ) : (
                      "No records"
                    )}
                  </div>
                  <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                    {currentPage > 1 ? (
                      <Pagination.Link
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage((p) => Math.max(1, p - 1));
                        }}
                      >
                        <Lucide icon="ChevronLeft" className="w-4 h-4" />
                      </Pagination.Link>
                    ) : null}
                    <Pagination.Link active>{currentPage}</Pagination.Link>
                    {currentPage < totalPages ? (
                      <Pagination.Link
                        onClick={(e) => {
                          e.preventDefault();
                          setCurrentPage((p) => p + 1);
                        }}
                      >
                        <Lucide icon="ChevronRight" className="w-4 h-4" />
                      </Pagination.Link>
                    ) : null}
                  </Pagination>
                  <FormSelect
                    className="sm:w-24 rounded-[0.5rem]"
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                  >
                    {PAGE_SIZES.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </FormSelect>
                </div>
              </div>
            </div>
          </div>
        </div>

        <Dialog
          open={receiptModalOpen}
          onClose={() => {
            if (receiptVerifying) return;
            setReceiptModalOpen(false);
            setReceiptError("");
            setReceiptDetails(null);
          }}
        >
          <Dialog.Panel className="max-w-3xl">
            <Dialog.Title>
              <div className="font-medium">Bank-In Receipt Details</div>
            </Dialog.Title>
            <Dialog.Description className="px-5 py-4 space-y-3">
              {receiptError ? <div className="text-sm text-danger">{receiptError}</div> : null}
              {receiptLoading && !receiptDetails ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Lucide icon="Loader2" className="w-4 h-4 animate-spin" />
                  Loading receipt details...
                </div>
              ) : receiptDetails ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                    <div><span className="text-slate-500">Team:</span> {receiptDetails.teamName || "-"}</div>
                    <div><span className="text-slate-500">Sales Date:</span> {formatDateHeading(receiptDetails.salesDate)}</div>
                    <div><span className="text-slate-500">Submitted At:</span> {formatDateTime(receiptDetails.bankedAt)}</div>
                    <div><span className="text-slate-500">Receipts:</span> {receiptDetails.receiptCount}</div>
                    <div>
                      <span className="text-slate-500">Bank In Amount:</span>{" "}
                      {receiptDetails.bankInAmount === null ? "-" : moneyWithThousands(receiptDetails.bankInAmount)}
                    </div>
                    <div>
                      <span className="text-slate-500">Paid:</span>{" "}
                      {moneyWithThousands(Number(receiptDetails.paidAmount || 0))}
                    </div>
                    <div className="sm:col-span-2">
                      <span className="text-slate-500">Verification:</span>{" "}
                      {receiptDetails.bankInVerified ? (
                        <span className="inline-flex items-center gap-1 text-green-700 font-medium">
                          <Lucide icon="BadgeCheck" className="w-4 h-4" />
                          Verified
                          {receiptDetails.bankInVerifiedByName ? ` by ${receiptDetails.bankInVerifiedByName}` : ""}
                          {receiptDetails.bankInVerifiedAt ? ` · ${formatDateTime(receiptDetails.bankInVerifiedAt)}` : ""}
                        </span>
                      ) : (
                        <span className="text-slate-600">Not verified</span>
                      )}
                    </div>
                  </div>
                  {receiptDetails.remark || (receiptDetails.reasons && receiptDetails.reasons.length > 0) ? (
                    <div className="space-y-2">
                      {receiptDetails.remark ? (
                        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                          <div className="text-xs font-medium text-amber-800 mb-0.5">Remark</div>
                          <div className="text-slate-800 whitespace-pre-wrap">{receiptDetails.remark}</div>
                        </div>
                      ) : null}
                      {receiptDetails.reasons && receiptDetails.reasons.length > 0 ? (
                        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                          <div className="text-xs font-medium text-slate-500 mb-1">
                            Reason{receiptDetails.reasons.length > 1 ? "s" : ""} stated by supervisor
                          </div>
                          <div className="space-y-2">
                            {receiptDetails.reasons.map((log, idx) => (
                              <div key={`${log.createdAt}-${idx}`}>
                                <div className="text-slate-800 whitespace-pre-wrap">{log.reason}</div>
                                <div className="mt-0.5 text-[11px] text-slate-500">
                                  {log.supervisorName ? `${log.supervisorName} · ` : ""}
                                  {formatDateTime(log.createdAt)}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <div>
                    <div className="text-sm font-medium mb-2">
                      Bank-In / Transfer Slip Payments {receiptDetails.payments?.length ? `(${receiptDetails.payments.length})` : ""}
                    </div>
                    {!receiptDetails.payments || receiptDetails.payments.length === 0 ? (
                      <div className="text-sm text-slate-500">No payment record found.</div>
                    ) : (
                      <div className="space-y-3">
                        {receiptDetails.payments.map((p, idx) => (
                          <div key={p.id} className="border border-slate-200 rounded-md p-3">
                            <div className="flex items-center justify-between text-sm">
                              <div className="font-semibold text-slate-900">
                                Payment #{idx + 1} · {moneyWithThousands(p.amount)}
                              </div>
                              <div className="text-xs text-slate-500">{formatDateTime(p.createdAt)}</div>
                            </div>
                            {p.supervisorName ? (
                              <div className="mt-0.5 text-xs text-slate-500">By {p.supervisorName}</div>
                            ) : null}
                            {p.remark ? (
                              <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-2 text-sm">
                                <div className="text-[11px] font-medium text-slate-500 mb-0.5">Remark</div>
                                <div className="text-slate-800 whitespace-pre-wrap">{p.remark}</div>
                              </div>
                            ) : null}
                            {p.bankSlips.length === 0 ? (
                              <div className="mt-2 text-xs text-slate-500">No receipt image for this payment.</div>
                            ) : (
                              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                                {p.bankSlips.map((s) => (
                                  <a
                                    key={s.id}
                                    href={s.imageUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="block border border-slate-200 rounded-md overflow-hidden hover:border-primary/60"
                                  >
                                    <img
                                      src={s.imageUrl}
                                      alt="Bank receipt"
                                      loading="lazy"
                                      decoding="async"
                                      className="w-full h-32 object-contain bg-slate-50"
                                    />
                                    <div className="px-2 py-1 text-[10px] text-slate-500">{formatDateTime(s.createdAt)}</div>
                                  </a>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-sm text-slate-500">No receipt details available.</div>
              )}
            </Dialog.Description>
            <Dialog.Footer className="flex items-center justify-end gap-3">
              {receiptDetails &&
              !receiptDetails.bankInVerified &&
              Number(receiptDetails.remainingAmount ?? 0) <= SETTLED_REMAINING_EPS ? (
                <Button
                  variant="success"
                  className="text-white"
                  onClick={() => void verifyReceiptBankIn()}
                  disabled={receiptLoading || receiptVerifying}
                >
                  {receiptVerifying ? (
                    <>
                      <Lucide icon="Loader2" className="w-4 h-4 mr-2 animate-spin" />
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Lucide icon="BadgeCheck" className="w-4 h-4 mr-2" />
                      Verify
                    </>
                  )}
                </Button>
              ) : null}
              <Button
                variant="outline-secondary"
                onClick={() => {
                  setReceiptModalOpen(false);
                  setReceiptError("");
                  setReceiptDetails(null);
                  setReceiptVerifying(false);
                }}
                disabled={receiptVerifying}
              >
                Close
              </Button>
            </Dialog.Footer>
          </Dialog.Panel>
        </Dialog>
      </RoleGate>
    </ProtectedRoute>
  );
}
